export type ProductCode = 'cms' | 'crm' | 'workflow';
export type AppSnapshot = { userId: string; capturedAt: string; apps: { accountId: string; productCode: ProductCode; businessId: string }[] };
const storageKey = 'smoothbadger.app-navigation.v1';
const changed = 'smoothbadger-app-navigation-changed';
type Identity = { profile?: { sub?: string; auth_time?: number } };
type Saved = { session: string; snapshot: AppSnapshot };
let pending: { session: string; task: Promise<void> } | undefined;
let generation = 0;
export const appSnapshotEvent = changed;
export function readAppSnapshot(): AppSnapshot | null {
  try { const value = (JSON.parse(sessionStorage.getItem(storageKey) || 'null') as Saved | null)?.snapshot; return value && Array.isArray(value.apps) ? value : null; }
  catch { return null; }
}
export function clearAppSnapshot(): void {
  generation++;
  pending = undefined;
  sessionStorage.removeItem(storageKey);
  window.dispatchEvent(new Event(changed));
}
export async function ensureAppSnapshot(user: Identity | null, request: () => Promise<AppSnapshot>): Promise<void> {
  const userId = user?.profile?.sub;
  if (!userId) return;
  const session = `${userId}:${user?.profile?.auth_time ?? 'session'}`;
  try { if ((JSON.parse(sessionStorage.getItem(storageKey) || 'null') as Saved | null)?.session === session) return; } catch { /* replace invalid cache */ }
  if (pending?.session === session) return pending.task;
  const revision = ++generation;
  sessionStorage.removeItem(storageKey);
  window.dispatchEvent(new Event(changed));
  const task = (async () => {
    let snapshot: AppSnapshot = { userId, capturedAt: new Date().toISOString(), apps: [] };
    try {
      const value = await request();
      if (value.userId?.toLowerCase() === userId.toLowerCase() && Array.isArray(value.apps)) snapshot = value;
    } catch { /* Optional navigation must not block sign-in or poll a failing service. */ }
    if (revision !== generation) return;
    sessionStorage.setItem(storageKey, JSON.stringify({ session, snapshot } satisfies Saved));
    window.dispatchEvent(new Event(changed));
  })();
  pending = { session, task };
  try { await task; } finally { if (pending?.task === task) pending = undefined; }
}
export function otherAppLinks(snapshot: AppSnapshot | null, current: ProductCode, businessId?: string | null) {
  const apps = snapshot?.apps ?? [];
  const account = businessId ? apps.find(app => app.productCode === current && app.businessId.toLowerCase() === businessId.toLowerCase())?.accountId : undefined;
  if (businessId && !account) return [];
  const products = new Set(apps.filter(app => !account || app.accountId === account).map(app => app.productCode));
  const targets = [
    { product: 'cms' as const, name: 'CMS', url: import.meta.env.VITE_CMS_APP_URL || 'https://cms.smoothbadger.com' },
    { product: 'crm' as const, name: 'CRM', url: import.meta.env.VITE_CRM_APP_URL || 'https://crm.smoothbadger.com' },
    { product: 'workflow' as const, name: 'Workflow', url: import.meta.env.VITE_WORKFLOW_APP_URL || 'https://workflow.smoothbadger.com' }
  ];
  return targets.filter(app => app.product !== current && products.has(app.product));
}
